import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { DAY_OF_WEEK_LABELS, hourLabel, type DeliveryMode } from "../lib/notifications.js";

/**
 * Tenant admin — Notification routing (ADR-0171 §4.1).
 * Route: `/app/admin/notifications`.
 *
 * Three cards: the severity × role routing matrix, per-role floors, and the
 * tenant's digest-schedule default. Every control autosaves (PATCH per
 * change); a cell edit the hub refuses (below a floor) snaps back and the
 * error toast carries the server's sentence. Reset-to-defaults hard-deletes
 * the tenant's policy row — personal settings are untouched.
 */

interface PolicyResponse {
  policy: {
    routing: Record<string, Record<string, DeliveryMode>>;
    floors: Record<string, DeliveryMode>;
    digest_cadence: "daily" | "weekly" | null;
    digest_day_of_week: number | null;
    digest_hour: number | null;
  } | null;
  effective: {
    routing: Record<string, Record<string, DeliveryMode>>;
    floors: Record<string, DeliveryMode>;
    digest: { cadence: "daily" | "weekly"; day_of_week: number; hour: number };
  };
  defaults: {
    routing: Record<string, Record<string, DeliveryMode>>;
    floors: Record<string, DeliveryMode>;
    digest: { cadence: "daily" | "weekly"; day_of_week: number; hour: number };
  };
  vocabularies: { roles: string[]; severities: string[]; modes: DeliveryMode[] };
  timezone: string;
}

const MODE_LABEL: Record<DeliveryMode, string> = {
  immediate: "Immediately",
  bundled: "Bundled",
  digest: "Digest",
  none: "No email"
};

const FLOOR_LABEL: Record<DeliveryMode, string> = {
  none: "No floor",
  digest: "At least digest",
  bundled: "At least bundled",
  immediate: "Immediate"
};

const HOURS = Array.from({ length: 24 }, (_, h) => h);

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function AdminNotificationRouting() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [data, setData] = useState<PolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiFetch<PolicyResponse>("/api/tenant/admin/notification-policy"));
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** One PATCH per control change; the served response replaces local state,
   *  and a refusal rolls back to what the hub still holds. */
  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    try {
      setData(await apiFetch<PolicyResponse>("/api/tenant/admin/notification-policy", { method: "PATCH", body }));
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      await load(); // snap back to the hub's truth
    } finally {
      setBusy(false);
    }
  }

  async function resetToDefaults() {
    const ok = await confirm({
      title: "Reset notification routing to Atlas defaults?",
      detail: "Everyone's personal settings are kept.",
      confirmLabel: "Reset"
    });
    if (!ok) return;
    setBusy(true);
    try {
      setData(await apiFetch<PolicyResponse>("/api/tenant/admin/notification-policy", { method: "DELETE" }));
      toast.push({ kind: "success", message: "Routing reset to defaults." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const digestCadence = data?.effective.digest.cadence ?? "weekly";

  return (
    <PageLayout
      title="Notifications"
      actions={
        <button className="btnTertiary" onClick={() => void resetToDefaults()} disabled={busy || loading}>
          Reset to defaults
        </button>
      }
    >
      {error && <ErrorState detail={error} />}
      {loading && <LoadingState />}
      {!loading && !error && data && (
        <div style={{ display: "grid", gap: 12 }}>
          <div className="card adminList">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Routing</div>
            <div className="tableWrap">
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Severity</th>
                    {data.vocabularies.roles.map((role) => (
                      <th key={role}>{titleCase(role)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.vocabularies.severities.map((severity) => (
                    <tr key={severity}>
                      <td style={{ fontWeight: 600 }}>{titleCase(severity)}</td>
                      {data.vocabularies.roles.map((role) => (
                        <td key={role}>
                          <select
                            className="select"
                            value={data.effective.routing[role]?.[severity] ?? "bundled"}
                            disabled={busy}
                            onChange={(e) =>
                              void patch({ routing: { [role]: { [severity]: e.target.value } } })
                            }
                            aria-label={`Email for ${role} at ${severity} severity`}
                          >
                            {data.vocabularies.modes.map((mode) => (
                              <option key={mode} value={mode}>
                                {MODE_LABEL[mode]}
                              </option>
                            ))}
                          </select>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="help">
              How action-item email reaches people, by their role on the item and the item's severity. Everything
              always appears in the in-app inbox.
            </div>
          </div>

          <div className="card adminList">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Floors</div>
            <div className="tableWrap">
              <table className="table tableCompact">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th style={{ width: 220 }}>Floor</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vocabularies.roles.map((role) => (
                    <tr key={role}>
                      <td style={{ fontWeight: 600 }}>{titleCase(role)}</td>
                      <td>
                        <select
                          className="select"
                          value={data.effective.floors[role] ?? "none"}
                          disabled={busy}
                          onChange={(e) => void patch({ floors: { [role]: e.target.value } })}
                          aria-label={`Email floor for ${role}`}
                        >
                          {data.vocabularies.modes
                            .slice()
                            .sort((a, b) => ["none", "digest", "bundled", "immediate"].indexOf(a) - ["none", "digest", "bundled", "immediate"].indexOf(b))
                            .map((mode) => (
                              <option key={mode} value={mode}>
                                {FLOOR_LABEL[mode]}
                              </option>
                            ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="help">
              A floor is the least email a person in this role can receive, whatever they choose in their own settings.
            </div>
          </div>

          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Digest schedule default</div>
            <div className="inputRow" style={{ gap: 8 }}>
              <label className="field">
                <span className="label">Cadence</span>
                <select
                  className="select"
                  value={digestCadence}
                  disabled={busy}
                  onChange={(e) => void patch({ digest_cadence: e.target.value })}
                >
                  <option value="weekly">Weekly</option>
                  <option value="daily">Daily</option>
                </select>
              </label>
              <label className="field">
                <span className="label">Day</span>
                <select
                  className="select"
                  value={data.effective.digest.day_of_week}
                  disabled={busy || digestCadence !== "weekly"}
                  onChange={(e) => void patch({ digest_day_of_week: Number(e.target.value) })}
                >
                  {DAY_OF_WEEK_LABELS.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="label">Hour</span>
                <select
                  className="select"
                  value={data.effective.digest.hour}
                  disabled={busy}
                  onChange={(e) => void patch({ digest_hour: Number(e.target.value) })}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="help">
              When digests send by default, in your organization's timezone ({data.timezone}). People can pick their own
              schedule in their settings.
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
